import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPrice, logger } from "../lib/api";
import { ProductVisual } from "../components/ProductVisual";
import { FadeIn } from "../components/FadeIn";

const CONFIG = {
  "new-collection": {
    title: "The New Collection",
    overline: "Latest Release",
    tagline: "The newest marks of the maison — crafted for distinction.",
    query: "?is_new=true",
  },
  men: { title: "Men — The Archive", overline: "Archived", tagline: "Pieces that will not return.", query: "?category=men" },
  women: { title: "Women — The Archive", overline: "Archived", tagline: "Pieces that will not return.", query: "?category=women" },
  tracksuits: {
    title: "Tracksuits",
    overline: "01 — Heavyweight Collection",
    tagline: "Luxury in motion. Produced in controlled quantities.",
    query: "?category=tracksuits",
  },
  "leather-goods": { title: "Leather — The Archive", overline: "Archived", tagline: "Pieces that will not return.", query: "?category=leather-goods" },
  accessories: {
    title: "Accessories",
    overline: "02 — The Final Detail",
    tagline: "The finishing touches of distinction.",
    query: "?category=accessories",
  },
};

const PieceInfo = ({ product, index }) => (
  <div className="pt-5">
    <p className="text-[9px] uppercase tracking-[0.45em] text-capra-bronze">CF {String(index + 1).padStart(2, "0")}</p>
    <h3 className="font-display text-xl lg:text-2xl text-capra-ivory mt-2 leading-snug">{product.name}</h3>
    <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-capra-muted">{product.colors?.[0]}</p>
    <div className="mt-3 flex items-baseline gap-5">
      <p className="font-display text-lg text-capra-gold">{formatPrice(product.price)}</p>
      {product.stock <= 0 && <p className="text-[9px] uppercase tracking-[0.4em] text-capra-ivory/35">Sold Out</p>}
    </div>
  </div>
);

const FullPiece = ({ product, index }) => (
  <FadeIn>
    <Link to={`/product/${product.slug}`} className="group block" data-testid={`product-card-${product.slug}`}>
      <div className="overflow-hidden">
        <div className="aspect-[3/4] sm:aspect-[16/9] lg:h-[85vh] lg:aspect-auto transition-transform duration-[1.6s] ease-out group-hover:scale-[1.02]">
          <ProductVisual product={product} />
        </div>
      </div>
      <PieceInfo product={product} index={index} />
    </Link>
  </FadeIn>
);

const HalfPiece = ({ product, index, delay }) => (
  <FadeIn delay={delay}>
    <Link to={`/product/${product.slug}`} className="group block" data-testid={`product-card-${product.slug}`}>
      <div className="overflow-hidden">
        <div className="aspect-[3/4] transition-transform duration-[1.6s] ease-out group-hover:scale-[1.03]">
          <ProductVisual product={product} />
        </div>
      </div>
      <PieceInfo product={product} index={index} />
    </Link>
  </FadeIn>
);

/* Editorial rhythm: full-width piece, then a pair — repeating */
const editorialRows = (products) => {
  const rows = [];
  let i = 0;
  while (i < products.length) {
    if (rows.length % 2 === 0) {
      rows.push({ type: "full", items: [products[i]], startIndex: i });
      i += 1;
    } else {
      rows.push({ type: "pair", items: products.slice(i, i + 2), startIndex: i });
      i += 2;
    }
  }
  return rows;
};

export default function Category({ slug }) {
  const cfg = CONFIG[slug];
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/products${cfg.query}`)
      .then(({ data }) => setProducts(data))
      .catch((err) => logger.error("Category fetch failed", err))
      .finally(() => setLoading(false));
  }, [cfg.query]);

  return (
    <div data-testid={`category-page-${slug}`}>
      <section className="pt-40 lg:pt-56 pb-20 lg:pb-28 px-6 lg:px-16">
        <FadeIn className="max-w-[1500px] mx-auto">
          <p className="text-[10px] uppercase tracking-[0.5em] text-capra-bronze mb-6">{cfg.overline}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-capra-ivory tracking-[0.06em]">{cfg.title}</h1>
          <p className="mt-6 font-display italic text-base md:text-lg text-capra-muted">{cfg.tagline}</p>
        </FadeIn>
      </section>

      <section className="px-6 lg:px-16 max-w-[1500px] mx-auto pb-32 lg:pb-44">
        {(() => {
          if (loading) {
            return (
              <div className="grid lg:grid-cols-2 gap-10">
                {["cat-skeleton-1", "cat-skeleton-2"].map((k) => (
                  <div key={k} className="aspect-[3/4] bg-capra-surface animate-pulse" />
                ))}
              </div>
            );
          }
          if (products.length === 0) {
            return <p className="text-capra-muted italic font-display text-lg">The collection is being prepared.</p>;
          }
          return (
            <div className="space-y-24 lg:space-y-36" data-testid="category-product-grid">
              {editorialRows(products).map((row) =>
                row.type === "full" ? (
                  <FullPiece key={row.items[0].id} product={row.items[0]} index={row.startIndex} />
                ) : (
                  <div key={row.items[0].id} className="grid sm:grid-cols-2 gap-8 lg:gap-14">
                    {row.items.map((p, j) => (
                      <HalfPiece key={p.id} product={p} index={row.startIndex + j} delay={j * 0.1} />
                    ))}
                  </div>
                )
              )}
            </div>
          );
        })()}
      </section>
    </div>
  );
}
